import { QueryClient, MutationCache } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Shared cache window for queries that change rarely. Use this as
 * `staleTime` on `useQuery` calls where you want one consistent SLA
 * across the app — admin/config changes (roles, teams, agents, etc.)
 * surface within 10 minutes for users who didn't make the change
 * themselves. Same-session mutations should still call
 * `queryClient.invalidateQueries(...)` so the user who made the change
 * sees it immediately.
 */
export const CACHE_TIME_10_MINS = 10 * 60 * 1000;

/**
 * Tighter shared cache window for session-identity data (current user,
 * resolved permissions) where staleness is more user-visible than admin
 * config but a fetch per navigation would be wasteful.
 */
export const CACHE_TIME_5_MINS = 5 * 60 * 1000;

/**
 * Shorter cache window for volatile data that goes stale quickly — e.g.
 * computed appointment availability, which shifts as slots are booked or
 * the EHR re-syncs. Use as `staleTime` so a re-opened view re-fetches.
 */
export const CACHE_TIME_1_MIN = 60 * 1000;

/**
 * Poll interval for views that watch something happening right now — the
 * Live Calls list, which is a roster of calls in flight.
 *
 * Five seconds is the roster, NOT the conversation. Per-call detail
 * (transcript, who is speaking) arrives over the SSE stream in
 * `useLiveTranscript` and is not on this clock; polling faster here would
 * buy nothing a viewer can see, since a call's stage changes on the order
 * of tens of seconds. Pair it with a matching `staleTime` so a remount
 * inside the window reuses the cache instead of firing an extra request.
 */
export const POLL_TIME_LIVE = 5 * 1000;

/**
 * Poll interval for the live-call count badged on the sidebar while you are
 * NOT on a Calls page. The badge is on every page for every user who can
 * read calls, so it runs at a sixth of the live cadence there; on `/calls`
 * pages, where the number sits beside the calls themselves, it polls at
 * {@link POLL_TIME_LIVE}. See `liveCountPollInterval`.
 */
export const POLL_TIME_LIVE_BADGE = 30 * 1000;

/**
 * Singleton TanStack Query client. Lives in its own module so router
 * `beforeLoad` guards (which run outside the React tree) can call
 * `queryClient.ensureQueryData(...)` to share the same cache as React-side
 * `useQuery` consumers.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30 * 1000, // 30 seconds
      refetchOnWindowFocus: false,
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      /*
        A mutation whose failure already has a home on screen opts out with
        `meta: { suppressGlobalError: true }`. Without it the same message
        appears twice — once inline next to the control that failed, once as a
        toast — and the toast is the copy that disappears while the user is
        still reading the form.
      */
      if (mutation.meta?.suppressGlobalError === true) return;
      const message = error instanceof Error ? error.message : 'An unexpected error occurred';
      toast.error(message);
    },
  }),
});
